import React from 'react'
import {
  QuestionMarkCircleIcon,
  MagnifyingGlassIcon,
  UserPlusIcon,
  BuildingOfficeIcon,
  PhoneIcon,
  BookOpenIcon
} from '@heroicons/react/24/outline'
import { Link } from 'react-router-dom'
import SEO from '@components/common/SEO'

interface HelpCard {
  icon: React.ElementType
  title: string
  description: string
  link: string
  color: string
}

const HelpPage: React.FC = () => {
  const helpCategories: HelpCard[] = [
    {
      icon: BookOpenIcon,
      title: 'เริ่มต้นใช้งาน',
      description: 'คู่มือพื้นฐานสำหรับผู้ใช้งานใหม่ วิธีการค้นหาและใช้งานเว็บไซต์',
      link: '#getting-started',
      color: 'blue'
    },
    {
      icon: MagnifyingGlassIcon,
      title: 'วิธีค้นหาบริษัท',
      description: 'เทคนิคการค้นหาและกรองข้อมูลเพื่อหาบริษัทที่ตรงกับความต้องการ',
      link: '#search-tips',
      color: 'green'
    },
    {
      icon: UserPlusIcon,
      title: 'การสมัครสมาชิก',
      description: 'วิธีสมัครสมาชิก เข้าสู่ระบบ และจัดการบัญชีของคุณ',
      link: '#registration',
      color: 'purple'
    },
    {
      icon: BuildingOfficeIcon,
      title: 'การ Claim บริษัท',
      description: 'คู่มือสำหรับเจ้าของบริษัทที่ต้องการ Claim และจัดการข้อมูล',
      link: '#claim-company',
      color: 'amber'
    },
    {
      icon: PhoneIcon,
      title: 'ติดต่อเรา',
      description: 'ช่องทางติดต่อทีมงาน รายงานปัญหา และขอความช่วยเหลือ',
      link: '/contact',
      color: 'red'
    },
    {
      icon: QuestionMarkCircleIcon,
      title: 'คำถามที่พบบ่อย',
      description: 'คำตอบสำหรับคำถามที่ผู้ใช้งานมักถามบ่อย',
      link: '/faq',
      color: 'indigo'
    }
  ]

  const helpStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "ศูนย์ช่วยเหลือ - ไทยเซ็กคิวริตี้ไดเรกทอรี่",
    "description": "ศูนย์ช่วยเหลือและคู่มือการใช้งานไทยเซ็กคิวริตี้ไดเรกทอรี่"
  }

  const colorClasses: Record<string, { bg: string; text: string; hover: string }> = {
    blue: { bg: 'bg-blue-100', text: 'text-blue-600', hover: 'hover:bg-blue-50' },
    green: { bg: 'bg-green-100', text: 'text-green-600', hover: 'hover:bg-green-50' },
    purple: { bg: 'bg-purple-100', text: 'text-purple-600', hover: 'hover:bg-purple-50' },
    amber: { bg: 'bg-amber-100', text: 'text-amber-600', hover: 'hover:bg-amber-50' },
    red: { bg: 'bg-red-100', text: 'text-red-600', hover: 'hover:bg-red-50' },
    indigo: { bg: 'bg-indigo-100', text: 'text-indigo-600', hover: 'hover:bg-indigo-50' }
  }

  return (
    <>
      <SEO
        title="ศูนย์ช่วยเหลือ"
        description="ศูนย์ช่วยเหลือและคู่มือการใช้งานไทยเซ็กคิวริตี้ไดเรกทอรี่ - วิธีค้นหาบริษัท สมัครสมาชิก และ Claim บริษัท"
        keywords="ศูนย์ช่วยเหลือ, help center, คู่มือใช้งาน, วิธีใช้, tutorial"
      />

      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(helpStructuredData) }}
      />

      <div className="bg-gradient-to-b from-primary-50 to-white min-h-screen">
        {/* Hero Section */}
        <div className="bg-primary-600 text-white py-16">
          <div className="container-wrapper">
            <div className="max-w-3xl mx-auto text-center">
              <div className="flex justify-center mb-6">
                <QuestionMarkCircleIcon className="w-16 h-16" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                ศูนย์ช่วยเหลือ
              </h1>
              <p className="text-xl text-primary-100">
                คู่มือและวิธีการใช้งานเว็บไซต์อย่างละเอียด
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="container-wrapper py-12">
          <div className="max-w-6xl mx-auto">
            {/* Quick Help Cards */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-neutral-900 mb-6 text-center">
                เลือกหัวข้อที่ต้องการความช่วยเหลือ
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {helpCategories.map((category, index) => {
                  const Icon = category.icon
                  const colors = colorClasses[category.color]

                  return (
                    <Link
                      key={index}
                      to={category.link}
                      className={`bg-white rounded-lg shadow-sm border border-neutral-200 p-6 transition-all duration-200 hover:shadow-lg ${colors.hover}`}
                    >
                      <div className={`w-12 h-12 ${colors.bg} rounded-lg flex items-center justify-center mb-4`}>
                        <Icon className={`w-6 h-6 ${colors.text}`} />
                      </div>
                      <h3 className="text-lg font-bold text-neutral-900 mb-2">
                        {category.title}
                      </h3>
                      <p className="text-neutral-600 text-sm leading-relaxed">
                        {category.description}
                      </p>
                    </Link>
                  )
                })}
              </div>
            </div>

            {/* Detailed Guides */}
            <div className="space-y-12">
              {/* Getting Started */}
              <div id="getting-started" className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <BookOpenIcon className="w-6 h-6 text-blue-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-neutral-900">
                    เริ่มต้นใช้งาน
                  </h2>
                </div>

                <div className="space-y-4 text-neutral-700 leading-relaxed">
                  <div>
                    <h3 className="font-semibold text-lg text-neutral-900 mb-2">ขั้นตอนที่ 1: เข้าสู่หน้าแรก</h3>
                    <p>เปิดเว็บไซต์ <a href="/" className="text-primary-600 hover:underline">thaisecurity.directory</a> คุณจะพบช่องค้นหาหลักและตัวกรองต่างๆ</p>
                  </div>

                  <div>
                    <h3 className="font-semibold text-lg text-neutral-900 mb-2">ขั้นตอนที่ 2: ค้นหาบริษัท</h3>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>พิมพ์คำค้นหา เช่น "รักษาความปลอดภัย" หรือชื่อบริษัท</li>
                      <li>เลือกจังหวัดหรือพื้นที่ที่ต้องการ</li>
                      <li>กรองตามประเภทบริการที่สนใจ</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-semibold text-lg text-neutral-900 mb-2">ขั้นตอนที่ 3: ดูรายละเอียด</h3>
                    <p>คลิกที่การ์ดบริษัทเพื่อดูข้อมูลเพิ่มเติม เช่น บริการ พื้นที่ให้บริการ และข้อมูลติดต่อ</p>
                  </div>

                  <div>
                    <h3 className="font-semibold text-lg text-neutral-900 mb-2">ขั้นตอนที่ 4: ติดต่อบริษัท</h3>
                    <p>
                      ผู้ใช้ทั่วไป (Guest) จะเห็นข้อมูลพื้นฐาน หากต้องการเห็นข้อมูลติดต่อครบถ้วน
                      <Link to="/register" className="text-primary-600 hover:underline ml-1">สมัครสมาชิก (ฟรี)</Link>
                    </p>
                  </div>
                </div>
              </div>

              {/* Search Tips */}
              <div id="search-tips" className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <MagnifyingGlassIcon className="w-6 h-6 text-green-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-neutral-900">
                    เทคนิคการค้นหา
                  </h2>
                </div>

                <div className="space-y-4 text-neutral-700 leading-relaxed">
                  <div>
                    <h3 className="font-semibold text-lg text-neutral-900 mb-2">การค้นหาแบบพื้นฐาน</h3>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>พิมพ์คำค้นหา: "รักษาความปลอดภัย", "รปภ", "security"</li>
                      <li>ค้นหาตามชื่อบริษัท: พิมพ์ชื่อบริษัทที่ต้องการโดยตรง</li>
                      <li>ค้นหาตามจังหวัด: เลือกจังหวัดจากรายการ dropdown</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-semibold text-lg text-neutral-900 mb-2">การกรองข้อมูล (ทุกคนใช้ได้)</h3>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li><strong>ประเภทบริการ:</strong> รักษาความปลอดภัยอาคาร, โรงงาน, งานอีเวนต์, บอดี้การ์ด และอื่นๆ</li>
                      <li><strong>พื้นที่:</strong> จังหวัด, อำเภอ/เขต</li>
                      <li><strong>ขนาดบริษัท:</strong> พนักงาน 1-50, 51-200, 201+ คน</li>
                      <li><strong>การเรียงลำดับ:</strong> ตามความเกี่ยวข้อง, ชื่อ, ปีก่อตั้ง</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-semibold text-lg text-neutral-900 mb-2">เคล็ดลับเพิ่มเติม</h3>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>ใช้คำค้นหาที่ชัดเจน เช่น "รักษาความปลอดภัยโรงงาน กรุงเทพ"</li>
                      <li>มองหาเครื่องหมาย "ตรวจสอบแล้ว" สำหรับบริษัทที่น่าเชื่อถือ</li>
                      <li>เปรียบเทียบหลายบริษัทก่อนตัดสินใจ</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Registration */}
              <div id="registration" className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <UserPlusIcon className="w-6 h-6 text-purple-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-neutral-900">
                    การสมัครสมาชิก
                  </h2>
                </div>

                <div className="space-y-4 text-neutral-700 leading-relaxed">
                  <div>
                    <h3 className="font-semibold text-lg text-neutral-900 mb-2">วิธีสมัครสมาชิก (ฟรี)</h3>
                    <ol className="list-decimal list-inside space-y-2 ml-4">
                      <li>คลิกปุ่ม <Link to="/register" className="text-primary-600 hover:underline">"สมัครสมาชิก"</Link> ที่มุมขวาบน</li>
                      <li>กรอกข้อมูล: อีเมล, ชื่อ-นามสกุล, เบอร์โทร, รหัสผ่าน</li>
                      <li>ยอมรับข้อกำหนดการใช้งาน</li>
                      <li>คลิก "สมัครสมาชิก" - เข้าใช้งานได้ทันที ไม่ต้องยืนยันอีเมล</li>
                    </ol>
                  </div>

                  <div>
                    <h3 className="font-semibold text-lg text-neutral-900 mb-2">ประโยชน์ของสมาชิก</h3>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>✅ เห็นข้อมูลติดต่อครบถ้วน (เบอร์โทร, อีเมล, ไลน์)</li>
                      <li>✅ ใช้ฟีเจอร์ค้นหาขั้นสูง (filters, sorting)</li>
                      <li>✅ Claim บริษัทของตัวเอง</li>
                      <li>✅ บันทึกรายการบริษัทที่สนใจ (เร็วๆ นี้)</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-semibold text-lg text-neutral-900 mb-2">การจัดการบัญชี</h3>
                    <p>
                      หลังเข้าสู่ระบบ ไปที่ <Link to="/profile" className="text-primary-600 hover:underline">โปรไฟล์</Link> เพื่อ:
                    </p>
                    <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
                      <li>แก้ไขชื่อ-นามสกุล, เบอร์โทร</li>
                      <li>เปลี่ยนรหัสผ่าน</li>
                      <li>ดูประวัติการ Claim</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Claim Company */}
              <div id="claim-company" className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <BuildingOfficeIcon className="w-6 h-6 text-amber-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-neutral-900">
                    การ Claim บริษัท (สำหรับเจ้าของบริษัท)
                  </h2>
                </div>

                <div className="space-y-4 text-neutral-700 leading-relaxed">
                  <div>
                    <h3 className="font-semibold text-lg text-neutral-900 mb-2">ขั้นตอนการ Claim</h3>
                    <ol className="list-decimal list-inside space-y-2 ml-4">
                      <li><Link to="/register" className="text-primary-600 hover:underline">สมัครสมาชิก</Link> (ถ้ายังไม่มีบัญชี)</li>
                      <li>ค้นหาบริษัทของคุณในระบบ</li>
                      <li>คลิก "Claim บริษัทนี้" ในหน้ารายละเอียดบริษัท</li>
                      <li>กรอกข้อมูลและแนบเอกสารยืนยัน:
                        <ul className="list-disc list-inside ml-6 mt-1">
                          <li>ใบทะเบียนบริษัท (สำเนา)</li>
                          <li>บัตรประชาชนเจ้าของ/ผู้มีอำนาจ</li>
                          <li>หนังสือมอบอำนาจ (ถ้าไม่ใช่เจ้าของ)</li>
                        </ul>
                      </li>
                      <li>ส่งคำขอและรอ Admin ตรวจสอบ (1-3 วันทำการ)</li>
                    </ol>
                  </div>

                  <div>
                    <h3 className="font-semibold text-lg text-neutral-900 mb-2">บริษัทยังไม่มีในระบบ?</h3>
                    <p>สามารถส่งคำขอเพิ่มบริษัทใหม่ได้:</p>
                    <ol className="list-decimal list-inside space-y-1 ml-4 mt-2">
                      <li>เข้าสู่ระบบ → ไปที่ <Link to="/my-claims" className="text-primary-600 hover:underline">My Claims</Link></li>
                      <li>คลิก "เพิ่มบริษัทใหม่"</li>
                      <li>กรอกข้อมูลและแนบเอกสาร</li>
                      <li>ส่งคำขอให้ Admin ตรวจสอบ</li>
                    </ol>
                  </div>

                  <div>
                    <h3 className="font-semibold text-lg text-neutral-900 mb-2">หลัง Claim สำเร็จ คุณสามารถ:</h3>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>แก้ไขข้อมูลบริษัท (ผ่าน Change Request)</li>
                      <li>อัปเดทรูปภาพและโลโก้</li>
                      <li>เพิ่มรายละเอียดบริการ</li>
                      <li>เห็นสถิติการเข้าชมหน้าบริษัท (เร็วๆ นี้)</li>
                    </ul>
                  </div>

                  <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg">
                    <p className="text-amber-900 text-sm">
                      <strong>หมายเหตุ:</strong> การแก้ไขข้อมูลทุกครั้งต้องผ่าน Admin อนุมัติ
                      เพื่อรักษาคุณภาพและความถูกต้องของข้อมูลในระบบ
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom CTAs */}
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* FAQ CTA */}
              <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-lg shadow-lg p-8 text-white">
                <h3 className="text-xl font-bold mb-3">ยังไม่เจอคำตอบ?</h3>
                <p className="text-primary-100 mb-4 text-sm">
                  ดูคำถามที่พบบ่อยเพิ่มเติม
                </p>
                <Link
                  to="/faq"
                  className="inline-flex items-center justify-center px-5 py-2.5 bg-white text-primary-600 font-semibold rounded-lg hover:bg-primary-50 transition-colors duration-200 shadow-md text-sm"
                >
                  คำถามที่พบบ่อย
                </Link>
              </div>

              {/* Contact CTA */}
              <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-lg shadow-lg p-8 text-white">
                <h3 className="text-xl font-bold mb-3">ต้องการความช่วยเหลือ?</h3>
                <p className="text-green-100 mb-4 text-sm">
                  ติดต่อทีมงานของเราได้ตลอดเวลา
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center px-5 py-2.5 bg-white text-green-600 font-semibold rounded-lg hover:bg-green-50 transition-colors duration-200 shadow-md text-sm"
                >
                  ติดต่อเรา
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default HelpPage
